import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  DocumentTab,
  type FragmentHighlight,
  SearchHistoryItem,
  type SearchStateUpdate,
  TimeSeriesHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import {
  ChartSearchItem,
  documentTableRowToSearchItem,
  RecentItem,
  SearchableItem,
  SensorSearchItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import {
  createSavedChartTab,
  createTimeSeriesTab,
  findOpenTabForChart,
} from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import React, { createContext, useCallback, useContext, useMemo } from 'react'

export type SelectDocumentOptions = {
  documentId: string
  query?: string
  fragmentHighlight?: FragmentHighlight
  searchOriginQuery?: string
}

export type OpenNewTabOptions = SelectDocumentOptions & {
  pageNumber?: number
}

export interface SearchActionsContextType {
  onSelectDocument: (options: SelectDocumentOptions) => void
  onOpenNewTab: (options: OpenNewTabOptions) => void
  onOpenChatbot: () => void
  onSearchStateChange: (
    updates: SearchStateUpdate,
    options?: { tabId?: string }
  ) => void
  onAddRecentDocument: (document: DocumentTableRow) => void
  onAddRecentAsset: (asset: SearchableItem) => void
  onOpenSensorDashboard: (sensor: SensorSearchItem) => void
  onOpenChartDashboard: (chart: ChartSearchItem) => void
  onOpenAssetDashboard: (datapointIds: string[], label: string) => void
  onOpenLinkedDocuments: (
    documentIds: string[],
    referenceText: string,
    unitId?: string
  ) => void
  onAddSensorToExistingTab: (sensor: SensorSearchItem, tabId: string) => void
  onOpenTagSearchDashboard: () => void
  openTabs: DocumentTab[]
  onSwitchToTab: (tabId: string) => void
  recentItems: RecentItem[]
  onSnapshotSearchResults: (tabId: string, results: DocumentTableRow[]) => void
  isFavorited: (id: string) => boolean
  toggleFavorite: (doc: DocumentTableRow) => void
}

export type SearchActionsProviderProps = {
  children: React.ReactNode
  recentItems: RecentItem[]
  addRecentItem: (item: SearchableItem) => void
  onOpenChatbot: () => void
  onSnapshotSearchResults: (tabId: string, results: DocumentTableRow[]) => void
  isFavorited: (id: string) => boolean
  toggleFavorite: (doc: DocumentTableRow) => void
}

const SearchActionsContext = createContext<
  SearchActionsContextType | undefined
>(undefined)

export function SearchActionsProvider({
  children,
  recentItems,
  addRecentItem,
  onOpenChatbot,
  onSnapshotSearchResults,
  isFavorited,
  toggleFavorite,
}: SearchActionsProviderProps) {
  const {
    handleSelectDocument,
    addDocumentTab,
    activeTabId,
    isOnDocument,
    updateSearchHistoryItem,
    openTabs,
    setActiveTabId,
    addTab,
    pushToHistory,
    setPagination: setGlobalPagination,
  } = useKnowledgeDocumentUXContext()

  const handleAddRecentDocument = useCallback(
    (doc: DocumentTableRow) => {
      addRecentItem(documentTableRowToSearchItem(doc))
    },
    [addRecentItem]
  )

  const handleOpenSensorDashboard = useCallback(
    (sensor: SensorSearchItem) => {
      addRecentItem(sensor)

      const existingDashboardCount = openTabs.filter((tab) =>
        tab.id.startsWith('timeseries-tab-')
      ).length
      const dashboardLabel = `Dashboard ${existingDashboardCount + 1}`
      const newTab = createTimeSeriesTab([sensor.sensorId], dashboardLabel)
      addTab(newTab)
    },
    [addRecentItem, addTab, openTabs]
  )

  // Open the SAVED chart itself (full fidelity: colors, y-axis config, date
  // config) rather than a sensor-seeded tab of its sensor ids. `addTab`
  // focuses the existing tab when this chart is already open (deterministic
  // tab id per chart).
  const handleOpenChartDashboard = useCallback(
    (chart: ChartSearchItem) => {
      addRecentItem(chart)

      // A sensor-seeded tab that was upgraded in place by its first save keeps
      // its original uuid tab id, so addTab's deterministic-id dedup cannot
      // see it. Match on the chart id itself and focus the existing live
      // editor instead of mounting a duplicate that would clobber its saves.
      const existingTab = findOpenTabForChart(openTabs, chart.id)
      if (existingTab) {
        setActiveTabId(existingTab.id)
        return
      }

      const newTab = createSavedChartTab(
        chart.id,
        chart.displayName || chart.name
      )
      addTab(newTab)
    },
    [addRecentItem, addTab, openTabs, setActiveTabId]
  )

  const handleOpenAssetDashboard = useCallback(
    (datapointIds: string[], label: string) => {
      const existingDashboardCount = openTabs.filter((tab) =>
        tab.id.startsWith('timeseries-tab-')
      ).length
      const dashboardLabel = label || `Dashboard ${existingDashboardCount + 1}`
      const newTab = createTimeSeriesTab(datapointIds, dashboardLabel)
      addTab(newTab)
    },
    [addTab, openTabs]
  )

  const handleOpenLinkedDocuments = useCallback(
    (documentIds: string[], referenceText: string, unitId?: string) => {
      if (documentIds.length === 0 && !unitId) return

      setGlobalPagination({ pageIndex: 0, pageSize: 200 })
      const linkedDocsItem: SearchHistoryItem = {
        type: 'search',
        searchState: {
          contentFilter: 'documents',
          searchMode: 'normal',
          viewMode: 'table',
          query: '',
          filters: [],
          sorting: [{ id: 'documentTitle', desc: false }],
          pagination: { pageIndex: 0, pageSize: 200 },
          selectedDocumentId: null,
        },
        linkedDocuments: {
          referenceText,
          documentIds,
          unitId,
        },
      }

      const newTab: DocumentTab = {
        id: `linked-docs-${Date.now()}`,
        history: [linkedDocsItem],
        currentIndex: 0,
      }
      addTab(newTab)
    },
    [setGlobalPagination, addTab]
  )

  const handleAddSensorToExistingTab = useCallback(
    (sensor: SensorSearchItem, tabId: string) => {
      addRecentItem(sensor)

      const tab = openTabs.find((t) => t.id === tabId)
      if (!tab) return

      const currentItem = tab.history[tab.currentIndex]
      if (currentItem.type !== 'timeseries') return

      const tsItem = currentItem as TimeSeriesHistoryItem
      // Saved-chart tabs render from the resolved chart config; seeding extra
      // sensor ids onto them is ignored, so just focus the tab.
      if (tsItem.chartID) {
        setActiveTabId(tabId)
        return
      }
      const existingIds = new Set(tsItem.sensorIds)
      if (existingIds.has(sensor.sensorId)) {
        setActiveTabId(tabId)
        return
      }

      const updatedItem = {
        ...tsItem,
        sensorIds: [...tsItem.sensorIds, sensor.sensorId],
      }
      pushToHistory(tabId, updatedItem)
      setActiveTabId(tabId)
    },
    [addRecentItem, openTabs, pushToHistory, setActiveTabId]
  )

  const handleOpenTagSearchDashboard = useCallback(() => {
    const existingDashboardCount = openTabs.filter((tab) =>
      tab.id.startsWith('timeseries-tab-')
    ).length
    const dashboardLabel = `Dashboard ${existingDashboardCount + 1}`
    const newTab = createTimeSeriesTab([], dashboardLabel, undefined, true)
    addTab(newTab)
  }, [addTab, openTabs])

  const onSelectDocument = useCallback(
    ({
      documentId,
      query,
      fragmentHighlight,
      searchOriginQuery,
    }: SelectDocumentOptions) => {
      handleSelectDocument(
        documentId,
        query,
        fragmentHighlight,
        searchOriginQuery
      )
    },
    [handleSelectDocument]
  )

  const onOpenNewTab = useCallback(
    ({
      documentId,
      query,
      fragmentHighlight,
      searchOriginQuery,
      pageNumber,
    }: OpenNewTabOptions) => {
      addDocumentTab({
        documentId,
        pageNumber: pageNumber ?? fragmentHighlight?.pageNum,
        query,
        searchOriginQuery,
        fragmentHighlight,
      })
    },
    [addDocumentTab]
  )

  const onSearchStateChange = useCallback(
    (updates: SearchStateUpdate, options?: { tabId?: string }) => {
      const tabId = options?.tabId ?? activeTabId
      if (!options?.tabId && isOnDocument) {
        return
      }
      updateSearchHistoryItem(tabId, updates)
    },
    [activeTabId, isOnDocument, updateSearchHistoryItem]
  )

  const value = useMemo<SearchActionsContextType>(
    () => ({
      onSelectDocument,
      onOpenNewTab,
      onOpenChatbot,
      onSearchStateChange,
      onAddRecentDocument: handleAddRecentDocument,
      onAddRecentAsset: addRecentItem,
      onOpenSensorDashboard: handleOpenSensorDashboard,
      onOpenChartDashboard: handleOpenChartDashboard,
      onOpenAssetDashboard: handleOpenAssetDashboard,
      onOpenLinkedDocuments: handleOpenLinkedDocuments,
      onAddSensorToExistingTab: handleAddSensorToExistingTab,
      onOpenTagSearchDashboard: handleOpenTagSearchDashboard,
      openTabs,
      onSwitchToTab: setActiveTabId,
      recentItems,
      onSnapshotSearchResults,
      isFavorited,
      toggleFavorite,
    }),
    [
      onSelectDocument,
      onOpenNewTab,
      onOpenChatbot,
      onSearchStateChange,
      handleAddRecentDocument,
      addRecentItem,
      handleOpenSensorDashboard,
      handleOpenChartDashboard,
      handleOpenAssetDashboard,
      handleOpenLinkedDocuments,
      handleAddSensorToExistingTab,
      handleOpenTagSearchDashboard,
      openTabs,
      setActiveTabId,
      recentItems,
      onSnapshotSearchResults,
      isFavorited,
      toggleFavorite,
    ]
  )

  return (
    <SearchActionsContext.Provider value={value}>
      {children}
    </SearchActionsContext.Provider>
  )
}

export function useSearchActions(): SearchActionsContextType {
  const ctx = useContext(SearchActionsContext)
  if (!ctx) {
    throw new Error(
      'useSearchActions must be used within a SearchActionsProvider'
    )
  }
  return ctx
}
